import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { config, Subscription } from 'rxjs';
import { Slide, SlideLine, Group } from 'app/models/slideLine.model';
import { SlideService } from 'app/services/slide-lines.service';
import { mainColumns, entryColumns, exitColumns, miscColumns } from 'app/config/slide-columns.config';
import { Modal } from 'bootstrap';  // ✅ add this line
import { forkJoin } from 'rxjs';
import { UnsavedChangesService } from '@app/services/unsaved-changes.service';
import { ProjectSlidesService } from '@app/services/project-slides.service';
import {
  TIMELINE_DIRECTIVE_DATLIST_VALUES,
  TIMELINE_DIRECTIVE_FIELD_HELP,
} from '@app/constants/timeline-marker-options';
import { alertSeedingHttpError, alertSeedingResult } from '@app/utils/seed-issues-alert';
import { extractHttpErrorMessage } from '@app/utils/http-error-message';
import { LanguageRow, LanguageService } from '@app/services/language.service';
import { ProjectCurrentLanguageService } from '@app/services/project-current-language.service';


@Component({
  selector: 'app-slidelines',
  templateUrl: './slidelines.component.html',
  styleUrl: './slidelines.component.scss'
})
export class SlidelinesComponent implements OnInit, OnDestroy {

  projectId = '';
  slideId = '';
  slide?: Slide;
  slideName = '';
  // Column-group visibility
  showEntry = true;
  showExit = true;
  showMisc = true;

  mainColumns = mainColumns;
  entryColumns = entryColumns;
  exitColumns = exitColumns;
  miscColumns = miscColumns;

  /** HTML datalist id + suggestion values for timeline directive fields */
  readonly timelineDirectiveDatalistId = 'pptgen-timeline-directives';
  readonly timelineDirectiveDatalistValues = TIMELINE_DIRECTIVE_DATLIST_VALUES;
  readonly timelineDirectiveHelp = TIMELINE_DIRECTIVE_FIELD_HELP;

  loading = false;
  sub?: Subscription;

  selectedGroup: string | null = null;
  availableGroups: Group[] = [];
  selectedNode: SlideLine | null = null;
  generatingAudio = false;
  pickingImages = false;
  regeneratingLineId: number | null = null;
  clearReseeding = false;
  finalizeLayouting = false;
  audioOptionKeys: string[] = [];
  hasAudioLines = false;
  hasImageLines = false;
  lessonLinked = false;
  audioAlreadyGenerated = false;
  translating = false;
  generatingTranslatedAudio = false;
  languages: LanguageRow[] = [];
  /** Bound to language dropdown; synced with ProjectCurrentLanguageService (not a getter). */
  currentLanguageCode = '';
  showTranslatedColumn = false;

  textEditDraft = '';
  textEditTitle = '';
  private textEditModal?: Modal;
  private textEditTarget: { node: SlideLine; field: 'value' | 'audioText' | 'translated' } | null = null;

  constructor(
    private route: ActivatedRoute,
    private slideSvc: SlideService,
    private unsaved: UnsavedChangesService,
    private projectSlidesSvc: ProjectSlidesService,
    private languageSvc: LanguageService,
    private projectCurrentLangSvc: ProjectCurrentLanguageService,
  ) { }

  @HostListener('input', ['$event'])
  @HostListener('change', ['$event'])
  onFieldEdited(ev: Event): void {
    if (this.loading || !this.slide) return;
    const t = ev.target;
    if (t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement || t instanceof HTMLSelectElement) {
      this.unsaved.setDirty(true);
    }
  }


  ngOnInit(): void {
    // debugger;
    this.sub = this.route.paramMap.subscribe(params => {
      this.projectId = params.get('projectId') || '';
      this.slideId = params.get('slideId') || '';
      this.currentLanguageCode = this.projectCurrentLangSvc.get(this.projectId);
      this.loadLessonLink();
      this.load();
      this.loadAudioOptions();
      this.loadLanguages();
      // Load groups only after route ids are known (avoids empty projectId/slideId race).
      this.slideSvc.getAvailableGroups(this.projectId, this.slideId).subscribe({
        next: (groups) => {
          this.availableGroups = Array.isArray(groups) ? groups : [];
          console.log(this.availableGroups);
        },
        error: () => {
          this.availableGroups = [];
        },
      });
    });
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.unsaved.setDirty(false);
  }


  load(): void {
    // debugger;
    this.loading = true;
    // you need to first get header and then details

    this.slideSvc.loadSlide(this.projectId, this.slideId).subscribe({
      next: (s) => {
        // Ensure group children have composite names for display
        //        s.lines = this.decorateNames(s.lines);
        console.log(s);
        this.slide = s;
        this.slideName = s.slideTitle || '';
        // Flatten all lines including children inside collapsed groups (UI flatten only walks open groups).
        const flat = this.flattenLines(s.lines || []);
        const audioNodes = flat.filter((n) => String(n?.main?.Type || '').toUpperCase() === 'AUDIO');
        this.hasAudioLines = audioNodes.length > 0;
        const imageNodes = flat.filter((n) => String(n?.main?.Type || '').toUpperCase() === 'IMAGE');
        this.hasImageLines = imageNodes.length > 0;
        /** TTS lines need mp3 in Value; static lines (no Audio text) need filename in Value only. */
        this.audioAlreadyGenerated =
          this.hasAudioLines &&
          audioNodes.every((n) => this.isAudioLineSatisfied(n));
        this.ensureTranslationLanguageSelected();
        this.loading = false;
        this.unsaved.setDirty(false);
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  private loadLessonLink(): void {
    if (!this.projectId) {
      this.lessonLinked = false;
      return;
    }
    this.projectSlidesSvc.getLinkedLesson(this.projectId).subscribe({
      next: (res: { linked?: boolean }) => {
        this.lessonLinked = !!(res && res.linked);
      },
      error: () => {
        this.lessonLinked = false;
      },
    });
  }

  private loadAudioOptions(): void {
    if (!this.projectId) {
      this.audioOptionKeys = [];
      return;
    }
    this.projectSlidesSvc.getProjectParameters(this.projectId).subscribe({
      next: (rows: any[]) => {
        const p = rows?.[0] || {};
        let opts: any[] = [];
        try { opts = JSON.parse(p.projectAudioOptions || '[]'); } catch { opts = []; }
        this.audioOptionKeys = Array.isArray(opts)
          ? opts.map((o) => String(o?.key ?? '').trim()).filter((k) => k.length > 0)
          : [];
      },
      error: () => {
        this.audioOptionKeys = [];
      }
    });
  }

  /** Replace all lines from the slide type template; removes DB lines and slide-related audio files (images kept). */
  clearAndReseedFromSlideType(): void {
    if (!this.slide) return;
    const st = String(this.slide.slideType || '').trim();
    if (!st) {
      alert('This slide has no slide type set. Set it on the project slides list first.');
      return;
    }
    let msg =
      'Remove all lines for this slide, delete English and translated audio files, clear all translations, and re-seed from the slide type? Images are not removed.';
    if (this.lessonLinked) {
      msg +=
        '\n\nThis slide is linked to a lesson. Re-seed will rebuild lines from the lesson/slide type and discard Creator edits on this slide (reorder, added groups, value changes). Prefer "Apply group layout" if you only need to refresh positions.';
    }
    if (!confirm(msg)) {
      return;
    }
    this.clearReseeding = true;
    this.projectSlidesSvc.seedLinesForOneSlide(this.projectId, Number(this.slideId), st, true).subscribe({
      next: (data) => {
        this.clearReseeding = false;
        alertSeedingResult(data, data?.message || 'Re-seeded.');
        this.load();
      },
      error: (err) => {
        this.clearReseeding = false;
        console.error('Clear and reseed failed', err);
        alertSeedingHttpError(err);
      },
    });
  }

  /** Recompute object-group X/Y/W/H after adding or reordering groups (Pass 2 layout). */
  applyGroupLayout(): void {
    if (!this.slide) return;
    this.finalizeLayouting = true;
    this.projectSlidesSvc.finalizeLinesForOneSlide(this.projectId, Number(this.slideId)).subscribe({
      next: (data) => {
        this.finalizeLayouting = false;
        alert(data?.message || 'Group layout applied.');
        this.load();
      },
      error: (err) => {
        this.finalizeLayouting = false;
        console.error('Apply group layout failed', err);
        alert(extractHttpErrorMessage(err) || 'Failed to apply group layout.');
      },
    });
  }

  pickLessonImagesFromLesson(): void {
    if (!this.slide) return;
    if (!this.lessonLinked) {
      alert('No lesson is linked to this project. Link a lesson from the project slides screen first.');
      return;
    }
    if (!this.hasImageLines) return;
    if (
      !confirm(
        'Copy image files from the linked lesson into this project for this slide? Files already in the project are not overwritten. IMAGE line values update to project paths when a file is resolved.'
      )
    ) {
      return;
    }
    this.pickingImages = true;
    this.projectSlidesSvc.pickLessonImagesForSlide(this.projectId, Number(this.slideId)).subscribe({
      next: (data) => {
        this.pickingImages = false;
        alert(data?.message || 'Images picked.');
        this.load();
      },
      error: (err) => {
        this.pickingImages = false;
        console.error('Pick lesson images failed', err);
        alert(extractHttpErrorMessage(err));
      },
    });
  }

  /** Static premade (blink.mp3): Value set, no Audio text. TTS: Audio text set; Value is mp3 after generate. */
  private isAudioLineSatisfied(node: SlideLine): boolean {
    const val = String(node?.main?.Value ?? '').trim();
    const script = String(node?.misc?.AudioText ?? '').trim();
    if (!script) {
      return /\.mp3$/i.test(val) && val !== '#';
    }
    return /\.mp3$/i.test(val);
  }

  loadLanguages(): void {
    this.languageSvc.getLanguages().subscribe({
      next: (rows: LanguageRow[]) => {
        this.languages = Array.isArray(rows) ? rows : [];
        this.ensureTranslationLanguageSelected();
      },
      error: () => {
        this.languages = [];
      },
    });
  }

  onCurrentLanguageChange(code: string): void {
    this.setCurrentLanguage(code);
  }

  private setCurrentLanguage(code: string): void {
    const next = String(code || '').trim();
    const prev = this.currentLanguageCode;
    this.currentLanguageCode = next;
    this.projectCurrentLangSvc.set(this.projectId, next);
    if (this.slide && next !== prev) {
      if (next) this.loadTranslations();
      else {
        this.showTranslatedColumn = false;
        this.clearTranslationsFromSlide();
      }
    }
  }

  /** Pick a translation language so Translate / Gen. audio buttons are usable. */
  private ensureTranslationLanguageSelected(): void {
    if (!this.projectId || !this.slideId || !this.slide) return;
    if (String(this.currentLanguageCode || '').trim()) {
      this.loadTranslations();
      return;
    }
    this.slideSvc.getTranslatedLanguagesForProjectSlide(this.projectId, this.slideId).subscribe({
      next: (rows: Array<{ LanguageShortCode?: string }>) => {
        const codes = (Array.isArray(rows) ? rows : [])
          .map((r) => String(r.LanguageShortCode || '').trim())
          .filter(Boolean);
        if (codes.length >= 1) {
          this.setCurrentLanguage(codes[0]);
          return;
        }
        if (this.languages.length === 1) {
          this.setCurrentLanguage(this.languages[0].LanguageShortCode);
        }
      },
      error: () => {
        if (this.languages.length === 1) {
          this.setCurrentLanguage(this.languages[0].LanguageShortCode);
        }
      },
    });
  }

  get translatedColumnLabel(): string {
    const code = this.currentLanguageCode;
    if (!code) return 'Translated';
    const row = this.languages.find((l) => l.LanguageShortCode === code);
    return row ? `Translated (${row.LanguageShortCode})` : `Translated (${code})`;
  }

  get translationModeActive(): boolean {
    return String(this.currentLanguageCode || '').trim().length > 0;
  }

  get currentLanguageLabel(): string {
    return this.projectCurrentLangSvc.labelFor(this.currentLanguageCode, this.languages);
  }

  translateSlideForLanguage(): void {
    if (!this.slide) return;
    const lang = String(this.currentLanguageCode || '').trim();
    if (!lang) {
      alert('Choose a translation language from the Current language dropdown (not EN — English source).');
      return;
    }
    const langName = this.languages.find((l) => l.LanguageShortCode === lang)?.LanguageName || lang;
    if (
      !confirm(
        `Translate this slide to ${langName}?\n\n` +
          '• Any existing translation for this slide and language will be removed and translated again.\n' +
          '• Translated audio files for this slide and language will be deleted.\n' +
          '• TEXT lines use PSLValue; AUDIO lines use PSLAudioValue.\n' +
          '• Audio is not generated here — review translated text, then use Generate translated audio.'
      )
    ) {
      return;
    }
    this.translating = true;
    this.slideSvc.translateSlideForLanguage(this.projectId, this.slideId, lang).subscribe({
      next: (data) => {
        this.translating = false;
        alert(data?.message || 'Translation complete.');
        this.loadTranslations();
      },
      error: (err) => {
        this.translating = false;
        alert(extractHttpErrorMessage(err) || 'Translation failed');
      },
    });
  }

  generateTranslatedAudioForSlide(): void {
    if (!this.slide) return;
    const lang = String(this.currentLanguageCode || '').trim();
    if (!lang) {
      alert('Choose a translation language from the Current language dropdown first.');
      return;
    }
    if (!this.hasAudioLines) return;
    const langName = this.languages.find((l) => l.LanguageShortCode === lang)?.LanguageName || lang;
    if (
      !confirm(
        `Generate TTS audio for translated ${langName} scripts on this slide?\n\n` +
          '• Uses PSLValTranslate from translatedval (after review/edit).\n' +
          '• Files are stored under the project language audio folder.'
      )
    ) {
      return;
    }
    this.generatingTranslatedAudio = true;
    this.slideSvc.generateTranslatedAudioForSlide(this.projectId, this.slideId, lang).subscribe({
      next: (data) => {
        this.generatingTranslatedAudio = false;
        alert(data?.message || 'Translated audio generated.');
      },
      error: (err) => {
        this.generatingTranslatedAudio = false;
        alert(extractHttpErrorMessage(err) || 'Translated audio generation failed');
      },
    });
  }

  generateAudioForSlide(): void {
    if (!this.slide) return;
    if (!this.hasAudioLines) return;
    const msg = this.audioAlreadyGenerated
      ? 'Regenerate TTS audio for this slide?\n\n' +
        '• Lines with Audio text (PSLAudioValue): Narakeet runs again and replaces the mp3 (even if an old file exists after reseed).\n' +
        '• Fixed sounds (e.g. blink.mp3 in Value only, no Audio text): unchanged.'
      : 'Generate audio for this slide?\n\n' +
        '• TTS lines (Audio text filled): Narakeet creates mp3 and stores filename in Value.\n' +
        '• Fixed sounds (filename in Value only, no Audio text): left as-is.';
    if (!confirm(msg)) return;
    this.generatingAudio = true;
    this.slideSvc.generateAudioForSlide(this.projectId, this.slideId, 'SLIDE').subscribe({
      next: (data) => {
        this.generatingAudio = false;
        this.showAudioGenerationOutcome(data);
        this.load(); // reload to see PSLValue updates
      },
      error: (err) => {
        this.generatingAudio = false;
        console.error('Audio generation failed', err);
        alert(extractHttpErrorMessage(err));
      }
    });
  }

  /** Summarize generate-audio API: per-line errors, skips when nothing was generated, etc. */
  private showAudioGenerationOutcome(data: any): void {
    const g = Number(data?.generated ?? 0);
    const sk = Number(data?.skipped ?? 0);
    const results = Array.isArray(data?.results) ? data.results : [];
    const failed = results.filter((r: any) => r && r.ok === false);
    const errN = failed.length || Number(data?.failed ?? 0);
    const lines: string[] = [];
    const head = data?.message
      ? `${data.message}\n(Generated: ${g}, skipped: ${sk}${errN ? `, errors: ${errN}` : ''})`
      : `Generated: ${g}, skipped: ${sk}${errN ? `, errors: ${errN}` : ''}`;
    lines.push(head);
    if (failed.length) {
      lines.push('', 'Line errors:');
      for (const r of failed) {
        const name = r.PSLName ? ` "${String(r.PSLName)}"` : '';
        lines.push(`• SR ${r.PSLSrNo}${name}: ${r.error || 'Failed'}`);
      }
    }

    if (g === 0 && sk > 0) {
      lines.push('', 'Skipped lines:');
      for (const r of results.filter((x: any) => x?.skipped)) {
        const name = r.PSLName ? ` "${String(r.PSLName)}"` : '';
        lines.push(`• SR ${r.PSLSrNo}${name}: ${r.reason || 'skipped'}${r.file ? ` — ${r.file}` : ''}`);
      }
    }

    if (g === 0 && !failed.length && !sk && results.length) {
      lines.push('', 'No lines were processed.');
    }

    alert(lines.join('\n'));
  }

  regenerateAudioForLine(node: SlideLine): void {
    if (!node) return;
    if (String(node?.main?.Type || '').toUpperCase() !== 'AUDIO') return;
    if (!confirm(`Regenerate audio for line ${node.id}? This will overwrite the mp3 file.`)) return;
    this.regeneratingLineId = node.id;
    this.slideSvc.regenerateAudioForLine(this.projectId, this.slideId, node.id).subscribe({
      next: (data) => {
        this.regeneratingLineId = null;
        alert(data?.message || (data?.skipped ? 'Skipped — no TTS script for this line.' : 'Audio regenerated'));
        this.load();
      },
      error: (err) => {
        this.regeneratingLineId = null;
        console.error('Audio regenerate failed', err);
        alert(err?.error?.message || err?.message || 'Audio regenerate failed');
      }
    });
  }

  private decorateNames(lines: SlideLine[], parentName?: string): SlideLine[] {
    return lines.map((l) => {
      const isGroup = l.kind === 'OG';
      const displayName = parentName ? `${parentName}.${l.PSLname}` : l.PSLname;
      const cloned: SlideLine = {
        ...l,
        PSLrawName: parentName ? l.PSLname : l.PSLrawName,
        PSLname: displayName,
        isOpen: l.isOpen ?? true,
        children: isGroup && l.children ? this.decorateNames(l.children, displayName) : l.children
      };
      return cloned;
    });
  }

  // ===== UI helpers =====
  flatten(lines: SlideLine[], acc: SlideLine[] = [], level = 0): { node: SlideLine; level: number }[] {
    const out: { node: SlideLine; level: number }[] = [];
    lines
      .sort((a, b) => a.order - b.order)
      .forEach(l => {
        out.push({ node: l, level });
        if (l.kind === 'OG' && l.isOpen && l.children) {
          out.push(...this.flatten(l.children, acc, level + 1));
        }
      });
    return out;
  }

  toggleGroup(node: SlideLine): void {
    if (node.kind === 'OG') {
      node.isOpen = !node.isOpen;
    }
  }

  canMoveUp(node: SlideLine, parent: SlideLine | null): boolean {
    const siblings = this.getSiblings(node, parent);
    return node.order > Math.min(...siblings.map(s => s.order));
  }

  canMoveDown(node: SlideLine, parent: SlideLine | null): boolean {
    const siblings = this.getSiblings(node, parent);
    return node.order < Math.max(...siblings.map(s => s.order));
  }

  canInsertAfter(node: SlideLine, parent: SlideLine | null): boolean {
    return node.PSLLevel == 0;
  }


  move(node: SlideLine, parent: SlideLine | null, delta: number): void {
    const siblings = this.getSiblings(node, parent).sort((a, b) => a.order - b.order);
    const idx = siblings.findIndex(s => s.id === node.id);
    const swapIdx = idx + delta;
    if (swapIdx < 0 || swapIdx >= siblings.length) return;

    const a = siblings[idx];
    const b = siblings[swapIdx];
    const tmp = a.order; a.order = b.order; b.order = tmp;

    const orderedIds = siblings.sort((x, y) => x.order - y.order).map(s => s.id);
    this.unsaved.setDirty(true);
    this.slideSvc.reorderSiblings(this.projectId, this.slideId, parent?.id ?? null, orderedIds).subscribe();
  }

  private getSiblings(node: SlideLine, parent: SlideLine | null): SlideLine[] {
    if (!this.slide) return [];
    if (!parent) return this.slide.lines;
    return parent.children || [];
  }

  insertLine(): void {
    this.slideSvc.addTopLevelLine(this.projectId, this.slideId, this.slideName).subscribe(newLine => {
      if (!this.slide) return;
      newLine.order = (this.slide.lines.at(-1)?.order ?? -1) + 1;
      newLine.PSLparentId = 0;
      newLine.kind = 'SL';
      newLine.PSLname = newLine.PSLname || `line_${Date.now()}`;
      this.slide.lines.push(newLine);
      this.unsaved.setDirty(true);
    });
  }

  /** Toolbar: open group picker to insert after the last top-level line. */
  openAddGroupAtEnd(): void {
    if (!this.availableGroups.length) {
      alert('No object groups are available for this slide. Check the slide type template.');
      return;
    }
    if (!this.slide?.lines?.length) {
      alert('No lines on this slide yet. Run Clear & seed lines first, then add a group.');
      return;
    }
    const last = this.slide.lines[this.slide.lines.length - 1];
    this.selectedGroup = null;
    this.prepareInsert(last);
  }

  prepareInsert(node: SlideLine) {
    this.selectedNode = node;
    this.selectedGroup = null;
    const modalEl = document.getElementById('insertGroupModal');
    if (modalEl) {
      const modal = Modal.getOrCreateInstance(modalEl);
      modal.show();
    }
  }

  confirmInsert() {
    if (!this.selectedNode) {
      alert('Could not determine where to insert the group. Close the dialog and try again.');
      return;
    }
    if (!this.selectedGroup) {
      alert('Select an object group from the list.');
      return;
    }

    const order = this.selectedNode.order;
    const linesrno = this.selectedNode.id;

    this.slideSvc.addTopLevelGroup(this.projectId, this.slideId, this.selectedGroup, order, linesrno)
      .subscribe({
        next: () => {
          this.selectedGroup = null;
          this.load();
        },
        error: (err) => alert(extractHttpErrorMessage(err) || 'Failed to insert object group.'),
      });
  }

  duplicate_line(node: SlideLine): void {
    const linename = node.PSLname;
    // we are not supporting duplicate as yet = so this line is commented - will have to remove of duplicate is supported

    if (node.kind == 'SL') {
      //this.slideSvc.addTopLevelLine(this.projectId, this.slideId, linename).subscribe(newLine => {
      //    if (!this.slide) return;
      //  newLine.order = (this.slide.lines.at(-1)?.order ?? -1) + 1;
      //  newLine.parentId = '';
      //  newLine.kind = 'SL';
      //  newLine.name = newLine.name || `line_${Date.now()}`;
      //  this.slide.lines.push(newLine);
      //});
    }
    if (node.kind == 'OG') {

      //this.slideSvc.addTopLevelGroup(this.projectId, this.slideId, linename, ).subscribe(group => {
      //  if (!this.slide) return;
      //  group.order = (this.slide.lines.at(-1)?.order ?? -1) + 1;
      //  group.parentId = '';
      //  group.kind = 'OG';
      //  group.name = group.name ;
      //  group.isOpen = true;
      //  this.slide.lines.push(group);
      //});
    }

  }


  delete(node: SlideLine, parent: SlideLine | null): void {
    if (!confirm('Delete this line?')) return;
    this.slideSvc.deleteLine(this.projectId, this.slideId, node.id, node.order).subscribe(() => {
      if (!this.slide) return;
      const arr = parent ? (parent.children || []) : this.slide.lines;
      const idx = arr.findIndex(l => l.id === node.id);
      if (idx >= 0) arr.splice(idx, 1);
      this.unsaved.setDirty(true);
    });
  }


  flattenLines(lines: SlideLine[]): SlideLine[] {
    return lines.flatMap(line =>
      line.kind === 'OG' && line.children?.length
        ? [line, ...this.flattenLines(line.children)]
        : [line]
    );
  }
  loadTranslations(): void {
    if (!this.slide || !this.translationModeActive) {
      this.showTranslatedColumn = false;
      this.clearTranslationsFromSlide();
      return;
    }
    const lang = this.currentLanguageCode;
    this.slideSvc.getTranslatedValsForProjectSlide(this.projectId, this.slideId, lang).subscribe({
      next: (rows: any[]) => {
        const list = Array.isArray(rows) ? rows : [];
        this.mergeTranslationsIntoSlide(list);
        this.showTranslatedColumn = list.length > 0;
      },
      error: () => {
        this.showTranslatedColumn = false;
        this.clearTranslationsFromSlide();
      },
    });
  }

  private clearTranslationsFromSlide(): void {
    if (!this.slide) return;
    for (const line of this.flattenLines(this.slide.lines)) {
      delete line.translatedText;
    }
  }

  private mergeTranslationsIntoSlide(rows: any[]): void {
    if (!this.slide) return;
    const bySr = new Map<number, any>();
    for (const r of rows) {
      const sr = Number(r.PSLSrNo);
      if (Number.isFinite(sr)) bySr.set(sr, r);
    }
    for (const line of this.flattenLines(this.slide.lines)) {
      const tv = bySr.get(line.id);
      if (tv) {
        line.translatedText = String(tv.PSLValTranslate ?? '');
      } else {
        delete line.translatedText;
      }
    }
  }

  previewText(value: unknown, maxLen = 80): string {
    const t = String(value ?? '').replace(/\s+/g, ' ').trim();
    if (!t) return '—';
    return t.length > maxLen ? `${t.slice(0, maxLen)}…` : t;
  }

  hasTranslatedCell(node: SlideLine): boolean {
    return node.translatedText !== undefined && node.translatedText !== null;
  }

  openTextEditor(field: 'value' | 'audioText' | 'translated', node: SlideLine): void {
    this.textEditTarget = { node, field };
    this.textEditDraft = this.readTextField(node, field);
    const labels = { value: 'Value (English)', audioText: 'Audio text (English)', translated: this.translatedColumnLabel };
    this.textEditTitle = `${labels[field]} — line ${node.id} (${node.PSLrawName || node.PSLname})`;
    const el = document.getElementById('textEditModal');
    if (el) {
      this.textEditModal = Modal.getOrCreateInstance(el);
      this.textEditModal.show();
    }
  }

  applyTextEditor(): void {
    if (!this.textEditTarget) return;
    this.writeTextField(this.textEditTarget.node, this.textEditTarget.field, this.textEditDraft);
    this.unsaved.setDirty(true);
    this.textEditModal?.hide();
    this.textEditTarget = null;
  }

  cancelTextEditor(): void {
    this.textEditModal?.hide();
    this.textEditTarget = null;
  }

  private readTextField(node: SlideLine, field: 'value' | 'audioText' | 'translated'): string {
    if (field === 'value') return String(node.main?.Value ?? '');
    if (field === 'audioText') return String(node.misc?.AudioText ?? '');
    return String(node.translatedText ?? '');
  }

  private writeTextField(node: SlideLine, field: 'value' | 'audioText' | 'translated', text: string): void {
    if (field === 'value') {
      node.main.Value = text;
      return;
    }
    if (field === 'audioText') {
      node.misc.AudioText = text;
      return;
    }
    node.translatedText = text;
  }

  updateAllLines() {
    if (!this.slide?.lines || this.slide.lines.length === 0) {
      alert('No lines to update. Seed or materialize lines for this slide first.');
      return;
    }
    const allLines = this.flattenLines(this.slide.lines);
    const lang = this.showTranslatedColumn ? this.currentLanguageCode : '';
    this.slideSvc.updateAllLines(this.projectId, this.slideId, allLines, lang).subscribe({
      next: (data: any) => {
        this.unsaved.setDirty(false);
        if (data?.message) {
          alert(data.message);
        }
      },
      error: (err) => alert(extractHttpErrorMessage(err) || 'Failed to update lines.'),
    });
  }

  trackById(_index: number, item: { node: SlideLine; level: number }): number { return item.node.id; }
}

